export type HealthHxItemType = { [key: string]: number | string | null };

export const healthHxTabs = {
    labs: 'Labs',
    meds: 'Meds',
    specVisits: 'Spec Visits',
    diagnosis: 'Diagnosis',
    vitals: 'Vitals',
    inptOutpt: 'Inpt / Outpt',
};

export const sortOptions = {
    [healthHxTabs.labs]: [
        { value: 'labs-newest-labTestDate-labResultType', label: 'Newest Test Date' },
        { value: 'labs-oldest-labTestDate-labResultType', label: 'Oldest Test Date' },
        { value: 'labs-labResultType', label: 'Name' },
    ],
    [healthHxTabs.meds]: [
        { value: 'meds-newest-fillEndDate-drugName', label: 'Newest Fill End Date' },
        { value: 'meds-oldest-fillEndDate-drugName', label: 'Oldest Fill End Date' },
        { value: 'meds-newest-fillStartDate-drugName', label: 'Newest Fill Start Date' },
        { value: 'meds-oldest-fillStartDate-drugName', label: 'Oldest Fill Start Date' },
        { value: 'meds-drugName', label: 'Name' },
        { value: 'meds-int-daysSupply', label: 'Days Supply' },
    ],
    [healthHxTabs.specVisits]: [
        { value: 'spec-newest-dos-claimSpecialtyClass', label: 'Newest Date of Service' },
        { value: 'spec-oldest-dos-claimSpecialtyClass', label: 'Oldest Date of Service' },
        { value: 'spec-claimSpecialtyClass', label: 'Specialty Type' },
        { value: 'spec-providerFullName', label: 'Provider Name' },
        { value: 'spec-procedureCodeDesc', label: 'Details' },
        { value: 'spec-serviceProcedureCode', label: 'Procedure Code' },
    ],
    [healthHxTabs.diagnosis]: [
        { value: 'diag-newest-dateOfService-diagCode', label: 'Newest Date of Service' },
        { value: 'diag-oldest-dateOfService-diagCode', label: 'Oldest Date of Service' },
        { value: 'diag-diagCode', label: 'Diagnosis Code' },
        { value: 'diag-int-icdSum', label: 'ICD (Sum)' },
        { value: 'diag-diagnosisDescription', label: 'Description' },
    ],
    [healthHxTabs.vitals]: [
        { value: 'vitals-newest-vitalDate-bmi', label: 'Newest Vitals Date' },
        { value: 'vitals-oldest-vitalDate-bmi', label: 'Oldest Vitals Date' },
    ],
    [healthHxTabs.inptOutpt]: [
        { value: 'inptOutpt-newest-admitDate-facility', label: 'Newest Admit Date' },
        { value: 'inptOutpt-oldest-admitDate-facility', label: 'Oldest Admit Date' },
        { value: 'inptOutpt-newest-dischargeDate-facility', label: 'Newest Discharge Date' },
        { value: 'inptOutpt-oldest-dischargeDate-facility', label: 'Oldest Discharge Date' },
        { value: 'inptOutpt-diagnosisCode', label: 'Diagnosis Code' },
        { value: 'inptOutpt-drg', label: 'DRG' },
        { value: 'inptOutpt-facility', label: 'Facility' },
        { value: 'inptOutpt-providerSpecialty', label: 'Provider Specialty' },
    ],
};

function compareText(prop: string, a: HealthHxItemType, b: HealthHxItemType) {
    if (!a[prop]) return 1;
    if (!b[prop]) return -1;
    return (a[prop] as string)?.localeCompare(b[prop] as string);
}

function compareDate(
    prop: string,
    fallback: string,
    a: HealthHxItemType,
    b: HealthHxItemType,
    reverse = false
) {
    const atime = new Date(a[prop] as string).getTime();
    const btime = new Date(b[prop] as string).getTime();
    // If the dates are the same, do a secondary sort on the title field
    if (reverse && atime === btime) return compareText(fallback, b, a);
    if (atime === btime) return compareText(fallback, a, b);
    return btime - atime;
}

export function healthHxSort(sortValue: string, a: HealthHxItemType, b: HealthHxItemType) {
    const parts = sortValue.split('-');
    if (parts[1] === 'newest') {
        return compareDate(parts[2], parts[3], a, b);
    }
    if (parts[1] === 'oldest') {
        return compareDate(parts[2], parts[3], b, a, true);
    }
    if (parts[1] === 'int') {
        return parseInt(a[parts[2]] as string) - parseInt(b[parts[2]] as string);
    }
    return compareText(parts[1], a, b);
}

const filterFields = {
    [healthHxTabs.labs]: ['labResultType', 'labTestName'],
    [healthHxTabs.meds]: ['drugName'],
    [healthHxTabs.specVisits]: [
        'claimSpecialtyClass',
        'providerFullName',
        'serviceProcedureCode',
        'procedureCodeDesc',
    ],
    [healthHxTabs.diagnosis]: ['diagCode', 'diagnosisDescription'],
    [healthHxTabs.inptOutpt]: ['facility', 'diagnosisCode', 'drg', 'providerSpecialty'],
};

export function healthHxFilter(historyList: HealthHxItemType[], filterVal: string, tab: string) {
    return historyList.filter((listItem) => {
        if (!filterVal) return true;
        return filterFields[tab].some(
            (field) =>
                typeof listItem[field] === 'string' &&
                (listItem[field] as string).toLowerCase().includes(filterVal)
        );
    });
}
